import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  input,
  signal
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  IonicModule,
  LoadingController,
  ModalController,
  ToastController,
  isPlatform,
} from "@ionic/angular";
import { ModeService } from "src/app/utils/mode.service";
import { Tab1Page } from "src/app/tab1/tab1.page";
import { TwoDecimal } from "src/app/utils/TwoDecimal";
import { PaymentAddRequest } from "../../interfaces/payment_add_request";
import { Store } from "@ngrx/store";
import { PaymentDto } from "../../interfaces/payment_dto";
import { InvoiceDto } from "../../interfaces/invoice_dto";
import { PdfPreviewComponent } from "../pdf-preview/pdf-preview.component";
import { loadCustomer } from "src/app/auth/ngrx/customer.actions";
import * as invoiceSelector from "../../invoice-ngrx/invoices.selectors";
import * as customerSelector from "../../../auth/ngrx/customer.selectors";
import * as paymentSelector from "../../payment-ngrx/payments.selectors";
import { PaymentPdfDto } from "../../interfaces/payment_pdf_dto";
import { Subject } from "rxjs";
import { AuthService } from "src/app/supabase/services/auth.service";
import { QrBillService } from "../../services/qr-bill.service";

@Component({
    selector: "app-new-payment-dialog",
    templateUrl: "./new-payment-dialog.component.html",
    styleUrls: ["./new-payment-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonicModule, FormsModule, ReactiveFormsModule]
})
export class NewPaymentDialogComponent implements OnDestroy {
  readonly payment = input<PaymentAddRequest | null>(null);

  payments: PaymentDto[] = [];
  invoices: InvoiceDto[] = [];

  paymentForm: FormGroup;
  today = new Date();
  formattedDate = this.today.toISOString().substr(0, 10);
  customerSignal = this.store.selectSignal(customerSelector.selectCustomer);

  isLoading$ = this.store.select(paymentSelector.selectIsLoading);
  destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public modalController: ModalController,
    private cdr: ChangeDetectorRef,
    public modeService: ModeService,
    private authService: AuthService,
    private store: Store<any>,
    private loadingCtrl: LoadingController,
    private qrBillService: QrBillService,
    private toastController: ToastController
  ) {
    this.paymentForm = this.fb.group({
      amount: [0, [Validators.required, Validators.min(0.05)]],
      payment_method: ["Bar", Validators.required],
      date: [this.formattedDate, Validators.required],
      student: ["", Validators.required],
    });
  }

  ngOnInit() {
    const payment = this.payment();
    if (payment) {
      this.paymentForm.patchValue(payment);
      this.paymentForm.controls["amount"].setValidators([
        Validators.required,
        Validators.min(0.05),
        Validators.max(payment.amount),
      ]);
    }

    this.paymentForm.get("student").valueChanges.subscribe((student) => {
      this.store
        .select(invoiceSelector.selectInvoicesByStudentId(student.student_id))
        .subscribe((invoices) => {
          this.invoices = invoices;
        });
      this.store
        .select(paymentSelector.selectPaymentsByStudentId(student.student_id))
        .subscribe((payments) => {
          this.payments = payments;
        });
    });

    this.cdr.detectChanges();
  }

  ionViewDidEnter() {
    this.store.dispatch(loadCustomer());
  }

  async previewPayment() {
    const loading = await this.loadingCtrl.create({
      message: "Erstelle Quittung...",
    });

    loading.present();

    var pdfSrc = "";
    var errorMessage = "Quittung konnte nicht erstellt werden";
    try {
      const originalPaymentFormValue = { ...this.paymentForm.value };

      var paymentPdfDto: PaymentPdfDto = {
        logo_url: this.customerSignal().logo_url,
        logo_alignment: this.customerSignal().logo_alignment,
        amount: originalPaymentFormValue.amount,
        payment_method: originalPaymentFormValue.payment_method,
        date: new Date(originalPaymentFormValue.date).toLocaleDateString("fr-CH"),
        currency: "CHF",
        payment_currency: "CHF",
        creditor: {
          brand: this.customerSignal().brand,
          name: this.customerSignal().forename + " " + this.customerSignal().surname,
          address: this.customerSignal().street,
          zip: this.customerSignal().postal_code,
          city: this.customerSignal().city,
          account: this.customerSignal().iban,
          country: "CH",
        },
        debtor: {
          name:
            originalPaymentFormValue.student.forename +
            " " +
            originalPaymentFormValue.student.surname,
          address: originalPaymentFormValue.student.street,
          zip: originalPaymentFormValue.student.postal_code,
          city: originalPaymentFormValue.student.city,
          country: "CH",
        }
      };

      try {
        const blobUrl = await this.qrBillService.generateReceipt(paymentPdfDto);
        pdfSrc = blobUrl;
      } catch (error) {
        if (error.message.includes("IBAN")) {
          errorMessage =
            "Die angegebene IBAN ist ungültig. Bitte überprüfe die IBAN in deinen Einstellungen und versuche es erneut.";
        }
        throw error;
      }

      let payment = { ...this.paymentForm.value };
      const paymentValue = this.payment();
      if (paymentValue?.invoice_id) payment.invoice_id = paymentValue.invoice_id;
      payment.user_id = await this.authService.getCurrentUserId();

      loading.dismiss();

      const modal = this.modalController.create({
        component: PdfPreviewComponent,
        componentProps: {
          pdfSrc: signal(pdfSrc),
          paymentAddRequest: signal(payment)
        },
        cssClass: "invoice-preview-modal",
      });
      modal.then((modal) => modal.present());

      (await modal).onDidDismiss().then((result) => {
        if (result.data) {
          this.cancel();
        }
      });
    } catch (error) {
      loading.dismiss();

      const toast = await this.toastController.create({
        message: errorMessage,
        duration: 3000,
        position: "bottom",
      });

      await toast.present();
    }
  }

  async cancel() {
    this.modalController.dismiss();
    if (await this.modalController.getTop()) {
      this.modalController.dismiss();
    }
  }

  async selectStudent() {
    const studentModal = await this.modalController.create(
      isPlatform("desktop") || isPlatform("phablet")
        ? {
            component: Tab1Page,
            componentProps: {
              isSelection: signal(true),
            },
            canDismiss: true,
            showBackdrop: true,
          }
        : {
            component: Tab1Page,
            componentProps: {
              isSelection: signal(true),
            },
            canDismiss: true,
            showBackdrop: true,
            backdropDismiss: true,
            breakpoints: [0, 0.6, 1],
            initialBreakpoint: 0.6,
          }
    );
    studentModal.onDidDismiss().then((result) => {
      if (result.data) {
        this.paymentForm.controls["student"].setValue(result.data);
        this.cdr.detectChanges();
      }
    });

    return studentModal.present();
  }

  twoDecimal(inputNumber: number) {
    return TwoDecimal(inputNumber);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
